import React from 'react';
import { updateBuilding } from 'shared/api/buildings';
import { CardControls, CardSection } from 'shared/components/cardControls';
import { useRequest } from 'shared/hooks';
import { dtvEntity, satellitePkgEntity } from 'shared/entityDetails';
import { BoxWrapper, GridWrapper } from 'shared/components/data-display/GridFormatter';
import {
  DateEditor,
  StringPriceEditor,
  TextEditor,
  withStaticSelectEditor,
} from 'shared/components/data-display/editors';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import { dateString } from 'shared/utils';
import { withAsyncSelect } from 'shared/components/data-display/withSelectors';
import { DataReducerActions } from 'shared/reducers';
import { satellitePaidBy } from './constants';

const servicesDescription = [
  {
    key: 'satellite_package_id',
    label: 'Package',
    formatter: (s) => s.satellite_package.name,
    editor: withAsyncSelect(satellitePkgEntity),
  },
  {
    key: 'dtv_package.name',
    formatter: (dtv) => dtv.dtv_package.name,
    label: 'DirecTV',
    editor: withAsyncSelect(dtvEntity),
    editKey: 'dtv_package_id',
  },
  {
    key: 'dtv_pid',
    label: 'PID',
    editor: TextEditor,
  },
];

const credentialDescription = [

  {
    key: 'dtv_user',
    label: 'Username',
    editor: TextEditor,
  },
  {
    key: 'dtv_pass',
    label: 'Password',
    editor: TextEditor,
  },
  {
    key: 'satellite_install_date',
    label: 'Install Date',
    formatter: (s) => dateString(s.satellite_install_date),
    editor: DateEditor,
  },
];

const feeDescription = [
  {
    key: 'dtv_trip_fee',
    label: 'Trip Fee',
    formatter: (s) => `$${s.dtv_trip_fee}`,
    editor: StringPriceEditor,
  },
  {
    key: 'satellite_paid_by',
    label: 'Paid By',
    editor: withStaticSelectEditor(satellitePaidBy),
  },
];

/**
 * Satellite and DirecTV settings.
 * @param {Function} buildingDispatch - Dispatch function for building state
 * @param {Object<string, *>} buildingState - The building state object
 */
function Satellite({
  buildingDispatch,
  buildingState: {
    data: building,
    dirty,
    editEnabled,
    updatedData,
  },
}) {
  const {
    request,
    isLoading,
  } = useRequest();

  const saveChanges = async () => {
    const updatedBuilding = await request(updateBuilding(building.id, updatedData), 'Updated building');
    if (updatedBuilding) {
      buildingDispatch({
        action: DataReducerActions.set,
        payload: updatedBuilding,
      });
    }
  };

  return (
    <CardSection editEnabled={editEnabled} saveChanges={saveChanges}>
      <CardControls
        clearChanges={() => buildingDispatch({ action: DataReducerActions.clear })}
        disabled={isLoading}
        dirty={dirty}
        editEnabled={editEnabled}
      />
      <BoxWrapper>
        <GridWrapper
          size={{
            lg: 6,
            xs: 12,
          }}
          title="Services"
        >
          <DataFormatter
            descriptions={servicesDescription}
            editing={editEnabled}
            data={building}
            updatedData={updatedData}
            updateValue={(key, value) => buildingDispatch({
              action: DataReducerActions.update,
              payload: {
                key,
                value,
              },
            })}
          />
        </GridWrapper>
        <GridWrapper
          size={{
            lg: 6,
            xs: 12,
          }}
          title="Credentials"
        >
          <DataFormatter
            descriptions={credentialDescription}
            editing={editEnabled}
            data={building}
            updatedData={updatedData}
            updateValue={(key, value) => buildingDispatch({
              action: DataReducerActions.update,
              payload: {
                key,
                value,
              },
            })}
          />
        </GridWrapper>
        <GridWrapper
          size={{
            lg: 6,
            xs: 12,
          }}
          title="Fees"
        >
          <DataFormatter
            descriptions={feeDescription}
            editing={editEnabled}
            data={building}
            updatedData={updatedData}
            updateValue={(key, value) => buildingDispatch({
              action: DataReducerActions.update,
              payload: {
                key,
                value,
              },
            })}
          />
        </GridWrapper>
      </BoxWrapper>
    </CardSection>
  );
}

export default Satellite;
